import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Activity, ArrowRight, Users } from "lucide-react";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhysicalExamFemale() {
  const [queue, setQueue] = useState(null);
  const [waitingTokens, setWaitingTokens] = useState([]);
  const [skippedTokens, setSkippedTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { speakWithGenderVoices } = useSpeechSynthesis();

  const fetchQueue = async () => {
    try {
      const token = localStorage.getItem("token");
      const [queueRes, tokensRes, skippedRes] = await Promise.all([
        axios.get(`${API}/departments/physical_exam_female/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        // Get tokens that completed blood collection (ready for physical exam)
        axios.get(`${API}/tokens?status=blood_done&gender=Female`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/departments/physical_exam_female/skipped`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setQueue(queueRes.data);
      setWaitingTokens(tokensRes.data);
      setSkippedTokens(skippedRes.data.skipped_tokens || []);
    } catch (error) {
      console.error("Failed to fetch queue", error);
    }
  };

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleCallNext = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/departments/call-next`,
        { department: "physical_exam_female" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const tokenNumber = response.data.token_number;
        toast.success(`Token ${tokenNumber} called!`);
        const message = `Token ${tokenNumber}, please proceed to Physical Examination for Female patients`;
        speakWithGenderVoices(message);
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi6Dzvjaj0APHGy98+CbTRAMUqvr8q5jHgU7n+n0xnksBSmAx/LZkEAKE1m48N2kUxQLSKXl8r5xIQYuf9T52ZBBChZmvvTinlEQC1Kp5O+wZB4EO5rj88h1KwUqfsXu1I4+CRJatPPepVYUC0me4fC+ciEGL4DN99mPQQoVZ77z4Z9RDwtRp+Lur2IdBTug6fTGeCsFKX/H8tiNQAoVYr/04p5SEAtPpe/us2YDBY6a4fLC');
        audio.play().catch(() => {});
        fetchQueue();
      } else {
        toast.info("No tokens in queue");
      }
    } catch (error) {
      toast.error("Failed to call next token");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!queue?.current_token) return;
    
    try {
      const token = localStorage.getItem("token");
      // Physical exam complete -> physical_done (ready for blood collection)
      await axios.put(
        `${API}/tokens/${queue.current_token}/status`,
        { status: "physical_done" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Physical examination completed for ${queue.current_token}!`);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to complete examination");
    }
  };

  const handleCompleteAndCallNext = async () => {
    if (!queue?.current_token) {
      await handleCallNext();
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      // Physical exam complete -> physical_done (ready for blood collection)
      await axios.put(
        `${API}/tokens/${queue.current_token}/status`,
        { status: "physical_done" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Examination completed for ${queue.current_token}!`);
      
      setTimeout(async () => {
        await handleCallNext();
      }, 500);
    } catch (error) {
      toast.error("Failed to complete examination");
    }
  };

  const handleSkip = async () => {
    if (!queue?.current_token) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/departments/skip-token`,
        { department: "physical_exam_female", token_number: queue.current_token },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info(`Token ${queue.current_token} skipped - will return after 5 tokens`);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to skip token");
    }
  };

  const handleRecall = async (tokenNumber) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/departments/recall-token`,
        { department: "physical_exam_female", token_number: tokenNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Token ${tokenNumber} recalled!`);
      const message = `Token ${tokenNumber}, please proceed to Physical Examination for Female patients`;
      speakWithGenderVoices(message);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to recall token");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="physical-exam-female-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-rose-600 flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900">Physical Examination</h1>
                <p className="text-rose-600 font-semibold mt-1">Female Patients</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => window.open('/display', '_blank')} className="h-10">
              <Activity className="mr-2 h-4 w-4" />Open Display
            </Button>
            <Button variant="outline" onClick={handleLogout} className="h-10">
              <LogOut className="mr-2 h-4 w-4" />Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="lg:col-span-2 border-rose-200">
            <CardHeader className="bg-rose-50">
              <CardTitle>Current Token</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {queue?.current_token ? (
                <div className="space-y-4">
                  <div className="text-center py-8 bg-rose-50 rounded-lg border-2 border-rose-300">
                    <div className="font-mono text-7xl font-black text-rose-900 tracking-widest">{queue.current_token}</div>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={handleCompleteAndCallNext} className="flex-1 h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid="complete-and-next-button">
                      Complete Examination & Call Next <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button onClick={handleSkip} variant="outline" className="h-14 px-6 text-lg font-semibold border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="skip-token-button">
                      Skip Token
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-12 text-slate-400"><p className="text-lg">No patient being examined</p><p className="text-sm mt-2">Click below to call the first patient</p></div>
                  <Button onClick={handleCallNext} disabled={loading || waitingTokens.length === 0} className="w-full h-14 text-lg font-semibold bg-rose-600 hover:bg-rose-700" data-testid="call-next-button">
                    Call Next Patient <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div><CardTitle>Waiting Queue</CardTitle><CardDescription>{waitingTokens.length} female patients waiting</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {waitingTokens.slice(0, 12).map((token) => (
                  <div key={token.token_number} className="p-4 bg-rose-50 rounded-lg border border-rose-200 text-center hover-lift">
                    <div className="font-mono text-xl font-bold text-rose-900">{token.token_number}</div>
                    <Badge className="mt-2 bg-rose-600 text-white">Female</Badge>
                  </div>
                ))}
              </div>
              {waitingTokens.length === 0 && <div className="text-center py-8 text-slate-400"><p>No female patients waiting</p></div>}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 border-amber-200">
            <CardHeader className="bg-amber-50">
              <CardTitle>Skipped Tokens</CardTitle>
              <CardDescription>
                {skippedTokens.length > 0
                  ? `${skippedTokens.length} tokens skipped - Auto-return after 5 or click to recall`
                  : "No skipped tokens"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {skippedTokens.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {skippedTokens.map((token) => (
                    <button
                      key={token.token_number}
                      onClick={() => handleRecall(token.token_number)}
                      className="p-4 bg-amber-50 rounded-lg border-2 border-amber-300 text-center hover:bg-amber-100 transition-colors cursor-pointer"
                    >
                      <div className="font-mono text-xl font-bold text-amber-900">{token.token_number}</div>
                      <Badge className="mt-2 bg-rose-600 text-white">Female</Badge>
                      <div className="text-xs text-amber-700 mt-1">Click to Recall</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <p>No tokens have been skipped</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}